/**
 * Task #1336 — Helper di gating self vs cross-user per le azioni Switchboard.
 * Estratto puro per renderlo testabile in isolamento (Switchboard.tsx ha grafo
 * di context troppo profondo per un test di componente leggero).
 *
 * Regole:
 *   - Azioni destructive cross-user (Hangup / Transfer / Barge) richiedono
 *     conferma esplicita via AlertDialog.
 *   - Le stesse azioni su self-call bypassano il dialog (l'utente ha già
 *     "confermato" cliccando il bottone dedicato sul proprio interno).
 *   - Listen / Whisper / Hold / Unhold non sono destructive: nessuna conferma
 *     forzata in questa funzione (la UI può comunque mostrarla per Listen/Whisper
 *     se richiesto).
 *
 * Self-detection (priorità):
 *   1) `owner_user_id === currentUserId` (semantica forte; robusta a shared
 *      extension, hot-desking, multi-device).
 *   2) Fallback: matching su extension number (`userExtension === targetExt`).
 */
export type SwitchboardSupervisorAction =
    | 'listen'
    | 'whisper'
    | 'barge'
    | 'hangup'
    | 'transfer'
    | 'hold'
    | 'unhold';

export interface SwitchboardCallTarget {
    answered_by_extension?: string | null;
    extension?: string | null;
    callee_number: string;
    owner_user_id?: string | null;
}

export interface SwitchboardGatingDecision {
    isSelfCall: boolean;
    requiresConfirm: boolean;
    targetExtension: string;
}

const DESTRUCTIVE: ReadonlySet<SwitchboardSupervisorAction> = new Set([
    'hangup',
    'transfer',
    'barge',
]);

export function decideSwitchboardGating(
    action: SwitchboardSupervisorAction,
    call: SwitchboardCallTarget,
    currentUserId: string | undefined,
    userExtension: string | undefined,
): SwitchboardGatingDecision {
    const targetExtension = call.answered_by_extension || call.extension || call.callee_number;
    const isSelfByOwner = !!(currentUserId && call.owner_user_id && call.owner_user_id === currentUserId);
    const isSelfByExt = userExtension !== undefined && targetExtension === userExtension;
    const isSelfCall = isSelfByOwner || isSelfByExt;
    const requiresConfirm = !isSelfCall && DESTRUCTIVE.has(action);
    return { isSelfCall, requiresConfirm, targetExtension };
}
